import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, AlertCircle } from 'lucide-react';
import Button from '../../components/Button';
import supabase from '../../lib/supabase';

export default function ValidateSecondEmail() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const validateEmail = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Get the current session
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        if (sessionError) throw sessionError;

        if (!session) {
          navigate('/auth');
          return;
        }

        // Check if we have a new email in the URL
        const newEmail = searchParams.get('email');
        if (!newEmail) {
          throw new Error('No email address found in the URL');
        }

        // Update the user's email
        const { error: updateError } = await supabase.auth.updateUser({
          email: newEmail
        });

        if (updateError) throw updateError;

        // Sign out the user so they can sign in with their new email
        await supabase.auth.signOut();
        navigate('/auth', { 
          state: { 
            message: 'Email updated successfully. Please sign in with your new email address.' 
          }
        });
      } catch (error: any) {
        console.error('Email validation error:', error);
        setError(error.message || 'Failed to validate email. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    validateEmail();
  }, [navigate, searchParams]);

  return (
    <div className="min-h-screen bg-surface px-4 py-8 flex flex-col">
      <div className="flex-1 flex flex-col justify-center max-w-sm mx-auto w-full">
        <div className="text-center mb-8">
          <div className="w-12 h-12 bg-accent rounded-xl flex items-center justify-center mx-auto mb-4">
            <Mail className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-2xl font-bold font-montserrat text-primary mb-2">
            Validating Email
          </h1>
          <p className="text-neutral text-sm font-inter">
            Please wait while we validate your new email address.
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-secondary-dark bg-opacity-10 text-secondary-dark text-sm flex items-start">
            <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {isLoading ? (
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-accent"></div>
          </div>
        ) : error ? (
          <div className="flex justify-center">
            <Button
              variant="primary"
              onClick={() => navigate('/auth')}
            >
              Return to Sign In
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}